import { C } from "@hikari/vault-core";

const Para = ({ children }) => (
    <p
        style={{
            color: C.muted,
            fontSize: "0.92rem",
            lineHeight: 1.75,
            margin: "0 0 1rem 0",
            fontFamily: C.font,
        }}
    >
        {children}
    </p>
);
const H = ({ children }) => (
    <h3
        style={{
            color: C.text,
            fontSize: "1.05rem",
            fontWeight: 500,
            margin: "2rem 0 0.75rem",
            fontFamily: C.font,
        }}
    >
        {children}
    </h3>
);
const Code = ({ children }) => (
    <code
        style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: "0.25rem",
            padding: "0.1rem 0.4rem",
            color: C.accent,
            fontSize: "0.85em",
        }}
    >
        {children}
    </code>
);

export default function Security() {
    return (
        <main
            style={{
                maxWidth: "760px",
                margin: "0 auto",
                padding: "4rem 1.5rem 5rem",
                fontFamily: C.font,
            }}
        >
            <div
                style={{
                    fontSize: "0.72rem",
                    letterSpacing: "0.3em",
                    color: C.accent,
                    marginBottom: "0.75rem",
                }}
            >
                TECHNICAL DEEP-DIVE
            </div>
            <h1
                style={{
                    fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                    fontWeight: 500,
                    margin: "0 0 0.5rem",
                    color: C.text,
                }}
            >
                Security model
            </h1>
            <div style={{ color: C.dim, fontSize: "0.82rem", marginBottom: "2.5rem" }}>
                A precise description of how Hikari Vault encrypts your data and what an attacker
                could and could not do at every layer.
            </div>

            <H>Threat model</H>
            <Para>
                We design against four kinds of adversary, in increasing order of capability:
            </Para>
            <Para>
                <strong style={{ color: C.text }}>(1) A passive network observer.</strong> Someone
                watching the wire between your device and our server. They see TLS-encrypted
                requests and learn that a vault was synced. They learn nothing about the contents
                or your identity beyond IP-level metadata.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>(2) A database breach.</strong> Someone who
                obtains a full dump of our MongoDB. They get a list of vault IDs (hashes),
                ciphertext blobs, and timestamps. None of this is reversible to plaintext or
                identity.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>(3) A compromised server.</strong> Someone who
                gains code-execution on our backend. They can serve malicious responses, but they
                still cannot decrypt vaults — the master key has never reached the server. They
                could attempt to ship a malicious frontend; this is mitigated by open-source code
                (M5), self-hosting (M5), and Subresource Integrity on production builds.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>(4) A targeted endpoint attack.</strong> Someone
                with code-execution on your device while your vault is unlocked. We cannot defend
                against this, and neither can any other vault. We minimise the attack surface by
                keeping master keys in <Code>CryptoKey</Code> handles with{" "}
                <Code>extractable: false</Code> and zeroing key material immediately after use.
            </Para>

            <H>Cryptographic primitives</H>
            <Para>
                All cryptography uses the browser-native{" "}
                <Code>SubtleCrypto</Code> implementation (Web Crypto API). No third-party crypto
                libraries are bundled with the engine.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>Symmetric encryption:</strong>{" "}
                <Code>AES-256-GCM</Code> with 96-bit random nonces per operation.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>Key derivation:</strong>{" "}
                <Code>PBKDF2-SHA256</Code> with 600,000 iterations, per-vault unique salt.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>Vault ID derivation:</strong>{" "}
                <Code>SHA-256</Code> over the normalised recovery phrase, hex-encoded.
            </Para>
            <Para>
                <strong style={{ color: C.text }}>Biometric binding:</strong> WebAuthn{" "}
                <Code>navigator.credentials.create()</Code> with a platform authenticator. The
                credential's raw ID is used as input to PBKDF2 to derive a key-encryption key
                (KEK) that wraps the master key per device.
            </Para>

            <H>Recovery phrase</H>
            <Para>
                A 12-word phrase drawn from the BIP-39 English wordlist (2048 words), giving 132
                bits of entropy — well above the 128-bit conventional security floor. The phrase
                is generated on your device using{" "}
                <Code>crypto.getRandomValues()</Code> and is the only input that the server cannot
                infer or reconstruct.
            </Para>

            <H>Master key lifecycle</H>
            <Para>
                A 256-bit master key is generated on first vault creation and wrapped multiple
                ways: by a PIN-derived KEK, by a biometric-derived KEK (one per enrolled device),
                and by a recovery-phrase-derived KEK. All wrappings encrypt the same master key,
                meaning any single unlock method suffices.
            </Para>
            <Para>
                The master key is imported into a non-extractable{" "}
                <Code>CryptoKey</Code> at unlock time and lives only for the duration of the
                session. Raw key bytes are zeroed (<Code>bytes.fill(0)</Code>) after import. The
                master key is never serialised, never sent, and never persisted.
            </Para>

            <H>What the server stores</H>
            <Para>
                A single MongoDB document per vault, of the form:
            </Para>
            <pre
                style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: "0.4rem",
                    padding: "1rem",
                    color: C.text,
                    fontSize: "0.8rem",
                    overflowX: "auto",
                    lineHeight: 1.6,
                }}
            >
{`{
  vault_id:              SHA-256(recovery_phrase)
  entries_encrypted:     AES-GCM(vault entries, master_key)
  trash_encrypted:       AES-GCM(trash, master_key)
  pin_wrapped_key:       AES-GCM(master_key, PBKDF2(pin))
  pin_hint:              optional plaintext hint
  recovery_wrapped_key:  AES-GCM(master_key, PBKDF2(recovery_phrase))
  bio_credentials:       [ { credential_id, wrapped_key, label } ]
  created_at, updated_at
}`}
            </pre>
            <Para>
                Every value except timestamps, the vault ID hash, the optional PIN hint, and the
                device label is ciphertext. The server cannot read any of it.
            </Para>

            <H>What the server does NOT store</H>
            <Para>
                Recovery phrases. PINs. Master keys. Biometric data. Email addresses. IP addresses
                (transient only). User-Agent strings. Cookies. Session tokens. Device fingerprints.
                Behavioural analytics. Anything that could be used to identify you.
            </Para>

            <H>Transport security</H>
            <Para>
                All traffic between client and server uses TLS 1.3. The production deployment is
                served from a <Code>.app</Code> TLD, which is on the HSTS preload list — modern
                browsers will refuse to connect over plaintext HTTP regardless of any
                misconfiguration on our end.
            </Para>

            <H>Responsible disclosure</H>
            <Para>
                If you find a security issue, please email{" "}
                <Code>security@hikarivault.app</Code>. A PGP key will be published at{" "}
                <Code>/.well-known/pgp-key.txt</Code> on launch. We will acknowledge within 72
                hours, coordinate a disclosure timeline (typically 90 days), and credit you in our
                advisory unless you prefer to remain anonymous. We don't currently offer a paid
                bounty; this will change post-audit.
            </Para>

            <H>Forthcoming</H>
            <Para>
                <strong style={{ color: C.text }}>Independent audit (post-M4):</strong> full report
                will be published verbatim, findings included.{" "}
                <strong style={{ color: C.text }}>Open source (M5):</strong> backend and frontend
                under MIT. <strong style={{ color: C.text }}>Reproducible builds (M5+):</strong>{" "}
                so users can verify the deployed bundle matches the published source.
            </Para>
        </main>
    );
}
